import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';

import Swal from 'sweetalert2';
import {
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const PAGE_SIZE = 10;

function ContactMessages() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // applied search (used for filtering)
  const [searchInput, setSearchInput] = useState(''); // what user types (apply on Enter/Search)
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [filterCampaign, setFilterCampaign] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  
  // Campaign search states
  const [campaignSearchTerm, setCampaignSearchTerm] = useState('');
  const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page,
        limit: PAGE_SIZE
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      if (filterCampaign !== 'all') {
        params.campaignId = filterCampaign;
      }

      const response = await api.get('/admin/contact-messages', { params });
      setMessages(response.data.messages || []);
      setTotal(response.data.total ?? 0);
      setTotalPages(response.data.totalPages ?? 1);

      if (response.data.page && response.data.page !== page) {
        setPage(response.data.page);
      }
    } catch (err) {
      setError('Failed to fetch contact messages');
      console.error('Error fetching messages:', err);
      setMessages([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterCampaign]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Filter campaigns based on search term
  useEffect(() => {
    if (campaignSearchTerm.trim() === '') {
      setFilteredCampaigns(campaigns);
    } else {
      const filtered = campaigns.filter(campaign =>
        campaign.name.toLowerCase().includes(campaignSearchTerm.toLowerCase())
      );
      setFilteredCampaigns(filtered);
    }
  }, [campaignSearchTerm, campaigns]);

  // Close dropdown when clicking outside (don't clear campaignSearchTerm so selected name stays)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCampaignDropdown && !event.target.closest('.campaign-dropdown-container')) {
        setShowCampaignDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCampaignDropdown]);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/admin/campaigns', { params: { limit: 500 } });
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/contact-messages/${messageId}`);
        await Swal.fire('Deleted!', 'The contact message has been deleted.', 'success');
        fetchMessages();
      } catch (err) {
        await Swal.fire('Error!', 'Failed to delete the message.', 'error');
        console.error('Error deleting message:', err);
      }
    }
  };

  const handleViewMessage = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign ? campaign.name : 'Unknown Campaign';
  };

  const handleCampaignSelect = (campaignId, campaignName = '') => {
    setFilterCampaign(campaignId);
    setPage(1);
    setShowCampaignDropdown(false);
    setCampaignSearchTerm(campaignId === 'all' ? '' : campaignName);
  };

  const handleCampaignClear = () => {
    setFilterCampaign('all');
    setPage(1);
    setShowCampaignDropdown(false);
    setCampaignSearchTerm('');
  };

  const handleCampaignSearchChange = (e) => {
    setCampaignSearchTerm(e.target.value);
    setShowCampaignDropdown(true);
  };

  const getSelectedCampaignName = () => {
    if (filterCampaign === 'all') return 'All Campaigns';
    const campaign = campaigns.find(c => c.id == filterCampaign);
    return campaign ? campaign.name : 'All Campaigns';
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchTerm(searchInput.trim());
    setPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <AdminLayout activeSection="CONTACT">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white mb-6">CONTACT MESSAGES</h1>
      </div>

      {/* Filters - uniform with Campaign List & Indemnity Form Submissions */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Filter by Campaign */}
          <div className="relative campaign-dropdown-container">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Campaign
            </label>
            <div className="flex">
              <input
                type="text"
                value={showCampaignDropdown ? campaignSearchTerm : getSelectedCampaignName()}
                onChange={handleCampaignSearchChange}
                onFocus={() => setShowCampaignDropdown(true)}
                placeholder="Search campaigns..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              {filterCampaign !== 'all' && (
                <button
                  type="button"
                  onClick={handleCampaignClear}
                  className="flex items-center px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  title="Clear campaign filter"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {showCampaignDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                <button
                  type="button"
                  onClick={() => handleCampaignSelect('all')}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                    filterCampaign === 'all' ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
                  }`}
                >
                  All Campaigns
                </button>
                {filteredCampaigns.map(campaign => (
                  <button
                    key={campaign.id}
                    type="button"
                    onClick={() => handleCampaignSelect(campaign.id, campaign.name)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      filterCampaign == campaign.id ? 'bg-teal-50 text-teal-700 font-medium' : 'text-gray-700'
                    }`}
                  >
                    {campaign.name}
                  </button>
                ))}
                {filteredCampaigns.length === 0 && campaignSearchTerm.trim() !== '' && (
                  <div className="px-4 py-3 text-sm text-gray-500">No campaigns found</div>
                )}
              </div>
            )}
          </div>

          {/* Search - apply on Enter or Search button (same as CampaignList) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name, email, or message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Search
              </button>
            </form>
          </div>

          {/* Clear filters */}
          <div className="flex items-end">
            {(searchTerm || filterCampaign !== 'all') ? (
              <button
                type="button"
                onClick={() => { setSearchInput(''); setSearchTerm(''); setPage(1); handleCampaignClear(); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        </div>

        {/* Stats - same as other pages */}
        <div className="mt-4 text-sm text-gray-600">
          {total === 0
            ? 'No messages'
            : `Showing ${(page - 1) * PAGE_SIZE + 1}-${Math.min(page * PAGE_SIZE, total)} of ${total} messages`}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  CONTACT INFO
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  MESSAGE PREVIEW
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  DATE SUBMITTED
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  ACTIONS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {messages.map((message) => (
                <tr key={message.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{message.full_name}</div>
                    <div className="text-sm text-gray-500">{message.email}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate">{message.message}</div>
                    <div className="text-xs text-gray-500 mt-1">{getCampaignName(message.campaign_id)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-600">
                    {formatDate(message.date_submitted)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <button
                        onClick={() => handleViewMessage(message)}
                        className="text-teal-600 hover:text-teal-800"
                        title="View Message"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete Message"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && messages.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm || filterCampaign !== 'all'
                  ? 'No messages match your search.'
                  : 'No contact messages yet. Messages will appear here when customers submit them.'}
              </p>
            </div>
          )}
        </div>

        {/* Footer with Pagination */}
        <div className="px-6 py-4 flex justify-between items-center border-t border-gray-200">
          <div />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Previous page"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                title="Next page"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Message Detail Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-start px-8 pt-8 pb-6">
              <h3 className="text-2xl font-bold text-gray-900">Contact Message Details</h3>
              <button
                onClick={() => setShowMessageModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="px-8 pb-8 overflow-y-auto flex-1">
              <div className="space-y-6">
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Full Name
                  </label>
                  <p className="text-lg text-gray-900">{selectedMessage.full_name}</p>
                </div>
                
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Email
                  </label>
                  <p className="text-lg text-gray-900">
                    <a href={`mailto:${selectedMessage.email}`} className="text-teal-600 hover:text-teal-800">
                      {selectedMessage.email}
                    </a>
                  </p>
                </div>
                
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Campaign
                  </label>
                  <p className="text-lg text-gray-900">
                    {getCampaignName(selectedMessage.campaign_id)}
                  </p>
                </div>
                
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Date Submitted
                  </label>
                  <p className="text-lg text-gray-900">
                    {formatDate(selectedMessage.date_submitted)}
                  </p>
                </div>
                
                <div>
                  <label className="inline-block px-4 py-2 bg-teal-700 text-white rounded-lg text-sm font-medium mb-3">
                    Message
                  </label>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {selectedMessage.message}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="px-8 pb-8 pt-4 flex justify-end space-x-3 border-t border-gray-100">
              <button
                onClick={() => setShowMessageModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleDeleteMessage(selectedMessage.id)}
                className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
              >
                Delete Message
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ContactMessages;
